/**
 * LeadSlaAlert
 * 
 * Componente para exibir alertas de SLA de leads.
 * Recebe notificações em tempo real via Socket.io quando leads estão em risco de violar SLA.
 * 
 * @see openspec/leads/design.md - Seção C3: Alertas de SLA
 */

import React, { useState, useEffect, useContext } from "react";
import {
  Snackbar,
  Badge,
  IconButton,
  Paper,
  Typography,
  Box,
  Slide,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Button,
  Popover,
  Chip,
  Tooltip
} from "@material-ui/core";
import { makeStyles, alpha } from "@material-ui/core/styles";
import { BellRing, CheckCircle2, X, ExternalLink } from "lucide-react";
import { Clock, AlertTriangle, AlertOctagon } from "lucide-react";
import { useHistory } from "react-router-dom";
import { AuthContext } from "../../context/Auth/AuthContext";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import IconButtonWithTooltip from "../ui/IconButtonWithTooltip";

const useStyles = makeStyles((theme) => ({
  alertBadge: {
    "& .MuiBadge-badge": {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText,
      animation: "$pulse 1.5s ease-in-out infinite"
    }
  },
  "@keyframes pulse": {
    "0%, 100%": {
      transform: "scale(1)",
      opacity: 1
    },
    "50%": {
      transform: "scale(1.2)",
      opacity: 0.8
    }
  },
  popover: {
    padding: 0,
    borderRadius: 16,
    maxWidth: 380,
    maxHeight: 500,
    overflow: "hidden",
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 32px rgba(0,0,0,0.4)"
      : "0 8px 32px rgba(0,0,0,0.12)",
    border: `1px solid ${theme.palette.divider}`,
    marginTop: theme.spacing(1),
  },
  header: {
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: "#fff",
    padding: theme.spacing(0.875, 1.25),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
  },
  headerIcon: {
    width: 24,
    height: 24,
    borderRadius: 5,
    backgroundColor: "rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  alertList: {
    maxHeight: 350,
    overflow: "auto",
    padding: 0
  },
  alertItem: {
    borderLeft: "4px solid",
    marginBottom: 1,
    "&.warning": {
      borderLeftColor: theme.palette.warning.main,
      backgroundColor: alpha(theme.palette.warning.main, 0.05)
    },
    "&.breached": {
      borderLeftColor: theme.palette.error.main,
      backgroundColor: alpha(theme.palette.error.main, 0.05)
    },
    "&.responded": {
      borderLeftColor: theme.palette.success.main,
      backgroundColor: alpha(theme.palette.success.main, 0.05)
    }
  },
  timeChip: {
    fontWeight: 600,
    fontSize: "0.75rem"
  },
  warningChip: {
    backgroundColor: alpha(theme.palette.warning.main, 0.15),
    color: theme.palette.warning.dark,
    borderColor: theme.palette.warning.main
  },
  breachedChip: {
    backgroundColor: alpha(theme.palette.error.main, 0.15),
    color: theme.palette.error.dark,
    borderColor: theme.palette.error.main
  },
  emptyState: {
    padding: theme.spacing(3),
    textAlign: "center",
    color: theme.palette.text.secondary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  footer: {
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "center"
  },
  snackbarContent: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  snackbarWarning: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText
  },
  snackbarError: {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText
  }
}));

function SlideTransition(props) {
  return <Slide {...props} direction="up" />;
}

/**
 * LeadSlaAlert
 * 
 * Componente autônomo que obtém socket e companyId do AuthContext.
 */
const LeadSlaAlert = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user, socket } = useContext(AuthContext);
  const companyId = user?.companyId;
  
  const [alerts, setAlerts] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "warning" });

  // Contar alertas não lidos
  const unreadCount = alerts.filter(a => !a.read).length;
  const hasBreached = alerts.some(a => a.status === "breached" && !a.read);

  // Receber alertas via Socket.io
  useEffect(() => {
    if (!socket || !companyId) return;

    const handleSlaEvent = (data) => {
      console.log("[SLA Alert] Evento recebido:", data);
      
      if (data.action === "first_response") {
        // Lead foi respondido - remover da lista de alertas ativos
        setAlerts(prev => prev.filter(a => a.ticketId !== data.ticketId));
        return;
      }

      if (data.action === "sla_warning" || data.action === "sla_breached") {
        const newAlert = {
          id: `${data.ticketId}-${Date.now()}`,
          ticketId: data.ticketId,
          status: data.action === "sla_breached" ? "breached" : "warning",
          elapsedMinutes: data.elapsedMinutes,
          remainingMinutes: data.remainingMinutes,
          targetMinutes: data.targetMinutes,
          percentUsed: data.percentUsed,
          createdAt: new Date(data.createdAt),
          receivedAt: new Date(),
          read: false
        };

        setAlerts(prev => {
          // Evitar duplicatas
          const existing = prev.find(a => a.ticketId === data.ticketId);
          if (existing) {
            return prev.map(a => 
              a.ticketId === data.ticketId ? { ...a, ...newAlert, id: a.id } : a
            );
          }
          return [newAlert, ...prev].slice(0, 50); // Máximo 50 alertas
        });

        // Exibir snackbar para alertas novos
        const message = data.action === "sla_breached"
          ? `🚨 SLA violado! Lead #${data.ticketId} aguarda há ${Math.round(data.elapsedMinutes)} min`
          : `⚠️ SLA em risco! Lead #${data.ticketId} - ${Math.round(data.remainingMinutes)} min restantes`;
        
        setSnackbar({
          open: true,
          message,
          severity: data.action === "sla_breached" ? "error" : "warning"
        });
      }
    };

    const channel = `company-${companyId}-lead-sla`;
    socket.on(channel, handleSlaEvent);

    return () => {
      socket.off(channel, handleSlaEvent);
    };
  }, [socket, companyId]);

  // Abrir popover
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    // Marcar todos como lidos
    setAlerts(prev => prev.map(a => ({ ...a, read: true })));
  };

  // Fechar popover
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Navegar para o ticket
  const handleOpenTicket = (ticketId) => {
    handleClose();
    history.push(`/tickets/${ticketId}`);
  };

  // Limpar alertas
  const handleClearAll = () => {
    setAlerts([]);
    handleClose();
  };

  // Fechar snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const open = Boolean(anchorEl);

  // Determinar ícone e cor com base no estado
  const getIconAndColor = () => {
    if (hasBreached) {
      return { icon: AlertOctagon, color: "#f44336" }; // Vermelho para SLA violado
    }
    if (unreadCount > 0) {
      return { icon: AlertTriangle, color: "#ff9800" }; // Amarelo para warning
    }
    return { icon: Clock, color: undefined }; // Padrão
  };

  const { icon: CurrentIcon, color: iconColor } = getIconAndColor();

  return (
    <>
      {/* Ícone com Badge - usando IconButtonWithTooltip padronizado */}
      <Badge 
        badgeContent={unreadCount} 
        color={hasBreached ? "error" : "secondary"}
        invisible={unreadCount === 0}
        overlap="circular"
      >
        <IconButtonWithTooltip
          icon={CurrentIcon}
          tooltip={unreadCount > 0 ? `${unreadCount} alerta(s) de SLA` : "Alertas de SLA"}
          onClick={handleClick}
          iconColor={iconColor}
          showWarning={hasBreached}
          className={hasBreached ? classes.alertBadge : ""}
        />
      </Badge>

      {/* Popover com Lista de Alertas */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right"
        }}
        PaperProps={{ className: classes.popover }}
      >
        {/* Header */}
        <Box className={classes.header}>
          <Box className={classes.headerTitle}>
            <div className={classes.headerIcon}>
              <BellRing size={14} />
            </div>
            <Typography style={{ fontSize: "0.8rem", fontWeight: 600, color: "#fff" }}>
              Alertas de SLA
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose} style={{ color: "rgba(255,255,255,0.7)", padding: 2 }}>
            <X size={14} />
          </IconButton>
        </Box>

        {/* Lista de Alertas */}
        {alerts.length > 0 ? (
          <>
            <List className={classes.alertList}>
              {alerts.map((alert, index) => (
                <React.Fragment key={alert.id}>
                  <ListItem 
                    className={`${classes.alertItem} ${alert.status}`}
                    button
                    onClick={() => handleOpenTicket(alert.ticketId)}
                  >
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2">
                            Lead #{alert.ticketId}
                          </Typography>
                          <Chip
                            size="small"
                            variant="outlined"
                            className={`${classes.timeChip} ${
                              alert.status === "breached" ? classes.breachedChip : classes.warningChip
                            }`}
                            label={
                              alert.status === "breached"
                                ? `⏱️ ${Math.round(alert.elapsedMinutes - alert.targetMinutes)}min atrasado`
                                : `⏳ ${Math.round(alert.remainingMinutes)}min restantes`
                            }
                          />
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="textSecondary">
                          Criado {formatDistanceToNow(alert.createdAt, { addSuffix: true, locale: ptBR })}
                        </Typography>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Tooltip title="Abrir ticket">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenTicket(alert.ticketId);
                          }}
                        >
                          <ExternalLink size={18} />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                  {index < alerts.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
            
            {/* Footer */}
            <Box className={classes.footer}>
              <Button 
                size="small" 
                color="secondary" 
                onClick={handleClearAll}
              >
                Limpar Todos
              </Button>
            </Box>
          </>
        ) : (
          <Box className={classes.emptyState}>
            <CheckCircle2 size={32} style={{ opacity: 0.2 }} />
            <Typography style={{ fontSize: "0.85rem", fontWeight: 500 }}>
              Nenhum alerta de SLA ativo
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ fontSize: "0.75rem" }}>
              Você será notificado quando leads estiverem em risco
            </Typography>
          </Box>
        )}
      </Popover>

      {/* Snackbar de Notificação */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        TransitionComponent={SlideTransition}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Paper
          elevation={6}
          className={snackbar.severity === "error" ? classes.snackbarError : classes.snackbarWarning}
          style={{ padding: "12px 16px", borderRadius: 8 }}
        >
          <Box className={classes.snackbarContent}>
            {snackbar.severity === "error" ? <AlertOctagon size={20} /> : <AlertTriangle size={20} />}
            <Typography variant="body2">
              {snackbar.message}
            </Typography>
            <IconButton 
              size="small" 
              onClick={handleCloseSnackbar}
              style={{ color: "inherit", marginLeft: 8 }}
            >
              <X size={18} />
            </IconButton>
          </Box>
        </Paper>
      </Snackbar>
    </>
  );
};

export default LeadSlaAlert;

